"use client";

import Image from "next/image";
import LeftArrowWhiteCircleIcon from "~/icons/left-arrow-white-circle.svg";
import styles from './KeySpecializationCardLp.module.scss'
import Carousel from "@/components/molecules/CustomCarousels/Carousel";
import { IModalActions, ModalTypes } from "@/types/modal";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { useModalDispatch } from "@/context/ModalContext";


type Specialization = {
  Image?: {
    enabled: boolean;
    source?: {
      data?: Array<{ attributes: { url: string } }>;
    };
    alt?: string;
    url?: string;
    title?: string;
  };
  Title?: {
    enabled: boolean;
    text: string;
  };
  SponsoredImage?: {
    data?: {
      attributes: {
        url: string;
      };
    };
  };
};

interface SpecializationEntry {
  attributes: { BasicDetails?: Record<string, unknown> };
}

const withProgramId = <T extends SpecializationEntry>(specialization: T, programId: string): T => ({
  ...specialization,
  attributes: {
    ...specialization.attributes,
    BasicDetails: { ...specialization.attributes.BasicDetails, PROGRAM_ID: programId },
  },
});

export function KeySpecializationCardLp({
  SlideContent,
  index,
  associatedSpecialization,
  programId,
}: {
  SlideContent: Specialization;
  index?: number;
  associatedSpecialization?: any;
  /** Replaces the specialization's PROGRAM_ID in the lead modal; unset keeps its own. */
  programId?: string;
}) {
  const title = SlideContent?.Image?.title?.replace(/\n/gi, "  \n");
  const modalDispatch = useModalDispatch();


  const GAEventsHandler = () => {
    if(!associatedSpecialization?.attributes?.BasicDetails){
      return
    }

    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMba,
        modalData: {
          associatedPrograms: [
            programId
              ? withProgramId(associatedSpecialization, programId)
              : associatedSpecialization,
          ],
          isLandingPage: true,
          isLambdaAPI: true,
          rfiCase: "THANK_YOU_PAGE_CASE"
         },
      },
    });
    
    pushToDataLayer({
      'event': 'card_interaction',
      'cta_text': title,
      'section_name': 'Key Specializations',
      'page_type': getGlobalStateValue('pageType')
    });
  }

  return (
    <div id={`specialization-card-${index}`}
      className={`${styles.SpecializationCard__background} flex flex-col justify-between`}
      onClick={GAEventsHandler}
    >
      {SlideContent?.Image?.enabled && (

        <div  className={`${styles.SpecializationCard__InnerImage} flex items-center sm:gap-[4rem] gap-2 w-full`}>
          <Image
            src={SlideContent?.Image?.source?.data?.[0]?.attributes?.url}
            alt={SlideContent?.Image?.alt || "Specialization Card"}
            width={60}
            height={60}
            title="Specialization Card"
          />

          {SlideContent?.SponsoredImage?.data?.attributes?.url && <Image
            src={SlideContent?.SponsoredImage?.data?.attributes?.url || ""}
            alt={"Specialization Card"}
            width={60}
            height={60}
            title="Specialization Card"
          />}

          {title?.includes("health") && SlideContent?.Image?.source?.data?.[0]?.attributes?.url && (
            <Image
              src={"https://static.amityonline.com/_s/plus_Image_a9a1b1cc3a.webp"}
              alt="extra icon"
              width={60}
              height={60}
              className={`${styles.SpecializationCard__plusImage}`}
            />
          )}
        </div>
      )}

      <div className="flex items-end justify-between gap-2">
        <h3 className={`${styles.SpecializationCard__InnerTitle}`}>
          {title}
        </h3>
        <div
          className={`shrink-0 ${styles.SpecializationCard__InnerShowMoreIcon}
        font-semibold`}
        >
         {associatedSpecialization?.attributes?.BasicDetails && <Image width={40} height={40} src={LeftArrowWhiteCircleIcon} alt="show more" title="show more" />}
        </div>
      </div>
    </div>
  );
}
type Props = {
  associatedSpecialization?: any[];
  programId?: string;
};

export default function KeySpecializationCarouselLp({
  associatedSpecialization,
  programId,
}: Props) {
  return (
    <section id="KeySpecializations" className={styles.root}>
      <Carousel
        config={{
          spaceBetween: 20,
          autoplay: {
            delay: 2000,
            status: true,
          },
          speed: 600,
          pagination: {
            className: "program_detail_carousel_pagination text-center my-5 ",
          },
          breakpoints: {
            320: {
              slidesPerView: 2,
              spaceBetween: 5,
            },
            481: {
              slidesPerView: 1.4,
              spaceBetween: 5,
            },
            768: {
              slidesPerView: 3.1,
              spaceBetween: 15,
            },
            1024: {
              slidesPerView: 4,
              spaceBetween: 10,
            },
            1200: {
              slidesPerView: 6,
              spaceBetween: 10,
            },
            1900: {
              slidesPerView: 6,
              spaceBetween: 10,
            },
            2560: {
              slidesPerView: 6,
              spaceBetween: 40,
            }
          },
        }}
        className={styles.specializationsCarousel}
      >
        {associatedSpecialization?.map((slideContent: SpecializationAttributes, index: number) => (
          <div key={index} className={styles.specialCardWrapper}>
            <KeySpecializationCardLp
              associatedSpecialization={associatedSpecialization[index]}
              SlideContent={slideContent.attributes}
              index={index}
              programId={programId}
            />
          </div>
        ))}
      </Carousel>
    </section>
  );
}
